// frontend/src/components/leads/labels/labelsModel.js
//
// The lead-labels feature's PURE logic - no React, no MUI, no requests. Every
// labels component and the page that wires them read their decisions from
// here, so each rule has one definition and one test file
// (labelsModel.test.js):
//
//   names      - normalizeLabelName / validateLabelName (trim + collapse
//                whitespace, 40 chars, no duplicate among the user's labels)
//   order      - sortLabels / moveLabel (the backend's `position`)
//   lookup     - labelsById / resolveLabels / earsFor (a card's ears)
//   selection  - triState / nextToggleAction (the panel's checkboxes over one
//                card or a whole selection)
//   errors     - mapApiError (a request failure -> a copy-table KEY)
//   starters   - STARTER_LABELS (the five default suggestions)
//   source     - labelSourceFor (whose labels a feed shows, if any)
//   admin      - defaultLabelKind / deletedDefaults (the admin user page)
//
// Label names are user data: nothing here logs.

import { LABEL_COLOR_KEYS } from "../../../theme/labelPalette";
import { isAccountLapsed } from "../../../utils/accountAccess";
import { labelsText } from "./labelsText";

/** The backend's name cap (after normalization). */
export const LABEL_NAME_MAX = 40;

/** The backend's per-user label cap - used until GET /lead-labels answers. */
export const DEFAULT_MAX_LABELS = 10;

/** How many ears a card shows before the "+N" ear. */
export const DEFAULT_MAX_EARS = 3;

/**
 * The five default suggestions, in the approved order and colours. Names
 * are copy-table KEYS (the component resolves them in the viewer's language),
 * so the created label is named in the language the user sees.
 *
 * `defaultKey` is the backend's `default_key` for the same label as SEEDED
 * (services/lead_labels.py `DEFAULT_LEAD_LABELS` - always the Hebrew names).
 */
export const STARTER_LABELS = Object.freeze([
  Object.freeze({ copyKey: "starterWaiting", color: "sky", defaultKey: "awaiting_reply" }),
  Object.freeze({ copyKey: "starterMeeting", color: "indigo", defaultKey: "meeting_scheduled" }),
  Object.freeze({ copyKey: "starterQuote", color: "amber", defaultKey: "quote_sent" }),
  Object.freeze({ copyKey: "starterNextMonth", color: "pink", defaultKey: "follow_up_next_month" }),
  Object.freeze({ copyKey: "starterDeal", color: "green", defaultKey: "deal_closed" }),
]);

/**
 * The starters with their names resolved from a copy table.
 *
 * @param {Record<string, string>} text a `labelsText(isHebrew)` table.
 * @returns {{name: string, color: string}[]}
 */
export const starterSuggestions = (text) =>
  STARTER_LABELS.map(({ copyKey, color }) => ({ name: text[copyKey], color }));

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

// THE SERVER'S NAME RULE - backend/app/schemas/lead_label_name.py
// `normalize_label_name` - mirrored step for step. A two-repo contract:
// change one side and the other in the same release.
//   1. REFUSE, on the RAW input, any control (Cc - TAB and newline included),
//      lone surrogate (Cs), private-use (Co) or noncharacter (U+FDD0-FDEF,
//      or low 16 bits FFFE/FFFF). Unassigned (Cn) is ACCEPTED (a newer
//      Unicode's emoji must pass).
//   2. STRIP every format character (Cf) and every
//      Default_Ignorable_Code_Point - EXCEPT, judged on the RAW sequence:
//        a. variation selectors U+FE00-FE0F, U+E0100-E01EF;
//        b. a ZWJ (U+200D) whose previous character (skipping one VS16
//           U+FE0F) is Extended_Pictographic or a skin-tone modifier
//           (U+1F3FB-1F3FF), AND whose next character is
//           Extended_Pictographic - ZWJ sequences (family, rainbow flag,
//           toned profession emoji) survive;
//        c. tag characters U+E0020-E007F in the unbroken run directly after
//           U+1F3F4 - subdivision flags survive.
//   3. NFC (after the strip: "e" + LRM + combining acute == e-acute).
//   4. Collapse whitespace runs to one space and trim. By now `\s` (u flag)
//      is exactly the server's set: Zs + U+2028/2029.
//   5. EMPTY if nothing is left, or every character is a combining mark
//      (\p{M}), a space (U+0020) or the braille blank (U+2800).
//   6. At most 40 CODE POINTS (an emoji counts as one).
// Uniqueness is case-insensitive on the result.
//
// Written with code-point numbers, never escapes of the characters: this
// file must stay plain ASCII.
const INVISIBLE = /[\p{Cf}\p{Default_Ignorable_Code_Point}]/u;
const PICTOGRAPHIC = /\p{Extended_Pictographic}/u;
const MARK = /\p{M}/u;
const REFUSED_CATEGORY = /\p{Cc}|\p{Cs}|\p{Co}/u;
const WHITESPACE_RUNS = /\s+/gu;
const ZWJ = 0x200d;
const VS16 = 0xfe0f;
const WAVING_BLACK_FLAG = 0x1f3f4;
const BRAILLE_BLANK = 0x2800;

const isVariationSelector = (cp) =>
  (cp >= 0xfe00 && cp <= 0xfe0f) || (cp >= 0xe0100 && cp <= 0xe01ef);
const isSkinToneModifier = (cp) => cp >= 0x1f3fb && cp <= 0x1f3ff;
const isTagChar = (cp) => cp >= 0xe0020 && cp <= 0xe007f;
const isNoncharacter = (cp) => (cp >= 0xfdd0 && cp <= 0xfdef) || (cp & 0xfffe) === 0xfffe;
const isPictographic = (cp) => PICTOGRAPHIC.test(String.fromCodePoint(cp));

/** Exception (b): the ZWJ at `i` sits between two emoji (RAW neighbours). */
function zwjJoinsEmoji(cps, i) {
  let j = i - 1;
  if (j >= 0 && cps[j] === VS16) j -= 1;
  if (j < 0) return false;
  const beforeOk = isPictographic(cps[j]) || isSkinToneModifier(cps[j]);
  return beforeOk && i + 1 < cps.length && isPictographic(cps[i + 1]);
}

/** Step 2: drop the invisibles, keeping exceptions (a)-(c). */
function stripInvisibles(text) {
  const chars = Array.from(text);
  const cps = chars.map((ch) => ch.codePointAt(0));
  const kept = [];
  let inTagRun = false;
  chars.forEach((ch, i) => {
    const cp = cps[i];
    const isTag = isTagChar(cp);
    // A tag run is unbroken: any non-tag character ends it (or starts one,
    // if it is the flag itself).
    if (!isTag) inTagRun = cp === WAVING_BLACK_FLAG;
    if (INVISIBLE.test(ch)) {
      const keep =
        isVariationSelector(cp) || // (a)
        (cp === ZWJ && zwjJoinsEmoji(cps, i)) || // (b)
        (isTag && inTagRun); // (c)
      if (!keep) return;
    }
    kept.push(ch);
  });
  return kept.join("");
}

/** Step 1 (on the RAW text). */
function hasRefusedChar(text) {
  return Array.from(text).some(
    (ch) => REFUSED_CATEGORY.test(ch) || isNoncharacter(ch.codePointAt(0))
  );
}

/** Steps 2-4 (no refusals): the value that is stored. */
const cleanName = (text) =>
  stripInvisibles(text).normalize("NFC").replace(WHITESPACE_RUNS, " ").trim();

/** Step 5. */
const isBlank = (name) =>
  Array.from(name).every(
    (ch) => MARK.test(ch) || ch === " " || ch.codePointAt(0) === BRAILLE_BLANK
  );

/**
 * Steps 2-4: the value that will be stored (and the one duplicates are
 * compared on). Never truncates: an over-long name is a validation error, not
 * a silent cut. The refusals (1, 5, 6) are `validateLabelName`'s.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeLabelName(raw) {
  if (raw === null || raw === undefined) return "";
  return cleanName(String(raw));
}

// Case-insensitive, locale-independent comparison key (plain toLowerCase -
// Hebrew has no case, and a locale-aware fold would make tests host-dependent).
const nameKey = (name) => normalizeLabelName(name).toLowerCase();

/**
 * @param {unknown} raw the typed name.
 * @param {{id: string, name: string}[]} [labels] the user's labels.
 * @param {{excludeId?: string}} [options] the label being renamed.
 * @returns {null|"empty"|"tooLong"|"invalidChars"|"duplicateName"} null =
 *   valid. Only "duplicateName" is also a copy-table key; "empty" /
 *   "tooLong" / "invalidChars" have no approved sentence and are expressed by
 *   the disabled save button (the input's maxLength makes "tooLong"
 *   unreachable by typing).
 */
export function validateLabelName(raw, labels = [], { excludeId } = {}) {
  // Step 1 runs on the RAW text, before anything is stripped or collapsed: a
  // TAB is refused, not quietly turned into a space.
  const text = raw === null || raw === undefined ? "" : String(raw);
  if (hasRefusedChar(text)) return "invalidChars";
  const name = cleanName(text);
  if (!name || isBlank(name)) return "empty";
  // Code points, not UTF-16 units - the server counts characters.
  if ([...name].length > LABEL_NAME_MAX) return "tooLong";
  const key = nameKey(name);
  const taken = (labels || []).some(
    (label) => label && label.id !== excludeId && nameKey(label.name) === key
  );
  return taken ? "duplicateName" : null;
}

// ---------------------------------------------------------------------------
// Order
// ---------------------------------------------------------------------------

const positionOf = (label) =>
  Number.isFinite(Number(label?.position)) ? Number(label.position) : Number.MAX_SAFE_INTEGER;

/**
 * The user's order: `position` ascending; ties (and missing positions) fall
 * back to name, then id, so the order is total and stable across renders.
 * Returns a NEW array.
 *
 * @template T
 * @param {T[]} labels
 * @returns {T[]}
 */
export function sortLabels(labels) {
  return [...(labels || [])].sort((a, b) => {
    const byPos = positionOf(a) - positionOf(b);
    if (byPos !== 0) return byPos;
    const byName = codeUnitCompare(a?.name, b?.name);
    return byName !== 0 ? byName : codeUnitCompare(a?.id, b?.id);
  });
}

// Plain code-unit order: deterministic on every host (no ICU / locale).
function codeUnitCompare(a, b) {
  const x = String(a ?? "");
  const y = String(b ?? "");
  if (x < y) return -1;
  return x > y ? 1 : 0;
}

/**
 * Move the item at `from` to `to` (both clamped). Returns a NEW array, or the
 * SAME array when nothing moves - so a caller can compare by identity.
 *
 * @template T
 * @param {T[]} list
 * @param {number} from
 * @param {number} to
 * @returns {T[]}
 */
export function moveLabel(list, from, to) {
  const n = list.length;
  if (n === 0) return list;
  const f = Math.max(0, Math.min(n - 1, from));
  const t = Math.max(0, Math.min(n - 1, to));
  if (f === t) return list;
  const next = [...list];
  const [item] = next.splice(f, 1);
  next.splice(t, 0, item);
  return next;
}

/** True when the two id lists are the same order (a no-op reorder). */
export const sameOrder = (a, b) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/** True while another label may be created. */
export const canCreateLabel = (labels, maxLabels = DEFAULT_MAX_LABELS) =>
  (labels?.length || 0) < maxLabels;

/**
 * The first colour no existing label uses, in swatch order - so a user who
 * never touches the swatches still gets distinguishable labels. Every colour
 * taken -> the first one.
 *
 * @param {{color: string}[]} labels
 * @returns {string}
 */
export function suggestColor(labels) {
  const used = new Set((labels || []).map((label) => label?.color));
  return LABEL_COLOR_KEYS.find((key) => !used.has(key)) || LABEL_COLOR_KEYS[0];
}

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/**
 * @param {{id: string}[]} labels
 * @returns {Map<string, object>}
 */
export function labelsById(labels) {
  const map = new Map();
  (labels || []).forEach((label) => {
    if (label && label.id !== undefined && label.id !== null) map.set(label.id, label);
  });
  return map;
}

/**
 * A lead's `label_ids` resolved to label objects, in the user's CURRENT label
 * order - never the order of `label_ids`, which reflects the label order at
 * the time the lead was fetched (a reorder must move every card's ears at
 * once). The order is `position` when the labels carry one, else the
 * insertion order of `byId` (built by `labelsById` from the ordered list).
 * Ids the user no longer has (deleted elsewhere, not yet refetched) are
 * skipped.
 *
 * @param {string[]|null|undefined} labelIds
 * @param {Map<string, object>} byId from `labelsById`, over the ordered list.
 * @returns {object[]}
 */
export function resolveLabels(labelIds, byId) {
  if (!Array.isArray(labelIds) || !byId) return [];
  const seen = new Set();
  const out = [];
  labelIds.forEach((id) => {
    if (seen.has(id)) return;
    seen.add(id);
    const label = byId.get(id);
    if (label) out.push(label);
  });
  if (out.length < 2) return out;
  const rank = new Map();
  let i = 0;
  byId.forEach((_, id) => {
    rank.set(id, i);
    i += 1;
  });
  // One key for all, never a mix of the two scales.
  const byPosition = out.every((label) => Number.isFinite(Number(label.position)));
  const orderOf = (label) => (byPosition ? Number(label.position) : rank.get(label.id));
  return out.sort((a, b) => orderOf(a) - orderOf(b));
}

/**
 * Split resolved labels into the ears a card shows and the "+N" remainder.
 *
 * @param {object[]} labels resolved, ordered.
 * @param {number} [max]
 * @returns {{shown: object[], moreCount: number, moreNames: string[]}}
 */
export function splitEars(labels, max = DEFAULT_MAX_EARS) {
  const list = labels || [];
  const cap = Math.max(0, Math.floor(max));
  const rest = list.slice(cap);
  return {
    shown: list.slice(0, cap),
    moreCount: rest.length,
    moreNames: rest.map((label) => label.name),
  };
}

/**
 * A card's ears straight from its `label_ids`.
 *
 * @param {string[]} labelIds
 * @param {Map<string, object>} byId
 * @param {number} [max]
 * @returns {{shown: object[], moreCount: number, moreNames: string[]}}
 */
export const earsFor = (labelIds, byId, max = DEFAULT_MAX_EARS) =>
  splitEars(resolveLabels(labelIds, byId), max);

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/**
 * How one label sits across the targeted leads - one card, or a selection.
 *
 * @param {string} labelId
 * @param {(string[]|null|undefined)[]} leadsLabelIds one `label_ids` per
 *   targeted lead.
 * @returns {"all"|"some"|"none"} no targets -> "none".
 */
export function triState(labelId, leadsLabelIds) {
  const { have, total } = labelCoverage(labelId, leadsLabelIds);
  if (have === 0) return "none";
  return have === total ? "all" : "some";
}

/**
 * How many of the targeted leads carry the label - the bulk panel's "k/n".
 *
 * @param {string} labelId
 * @param {(string[]|null|undefined)[]} leadsLabelIds one `label_ids` per lead.
 * @returns {{have: number, total: number}}
 */
export function labelCoverage(labelId, leadsLabelIds) {
  const lists = leadsLabelIds || [];
  let have = 0;
  lists.forEach((ids) => {
    if (Array.isArray(ids) && ids.includes(labelId)) have += 1;
  });
  return { have, total: lists.length };
}

/**
 * What a click on a label row does: a label every target already carries is
 * removed; anything else ("some" or "none") is added to all of them - the
 * Gmail rule, so one click always ends in a uniform state.
 *
 * @param {"all"|"some"|"none"} state
 * @returns {"add"|"remove"}
 */
export const nextToggleAction = (state) => (state === "all" ? "remove" : "add");

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

const DETAIL_TO_COPY = Object.freeze({
  label_limit_reached: "limitNote",
  label_name_taken: "duplicateName",
});

/**
 * A failed labels request -> the copy-table KEY to show. Reads the backend's
 * machine token (`detail` as a string, or `detail.code`), never an English
 * message. Anything unrecognised - network error, 5xx, 422 - is generic.
 *
 * @param {unknown} err an axios error (or anything thrown).
 * @returns {"limitNote"|"duplicateName"|"genericError"}
 */
export function mapApiError(err) {
  const detail = err?.response?.data?.detail;
  const token =
    typeof detail === "string"
      ? detail
      : detail && typeof detail === "object" && typeof detail.code === "string"
        ? detail.code
        : null;
  return token && Object.prototype.hasOwnProperty.call(DETAIL_TO_COPY, token)
    ? DETAIL_TO_COPY[token]
    : "genericError";
}

// ---------------------------------------------------------------------------
// Source - whose labels a feed shows
// ---------------------------------------------------------------------------

/**
 * Whose labels the dashboard feed shows:
 *   "owner"  - an admin in "all" mode (every feed, or one customer's feed via
 *              the user filter, which lives in that mode): each lead's OWNER's
 *              labels, READ-ONLY (`owner_label_ids` + the page's
 *              `owner_labels` map). Never editable - they are the customer's.
 *   "viewer" - the signed-in user's own feed (every customer, an admin in
 *              "my leads" mode), not lapsed: their own labels, editable.
 *   "none"   - no labels at all (signed out, a lapsed account).
 * A customer is never "owner", so `owner_*` fields are ignored for them even
 * if a payload carried them.
 *
 * @param {object|null|undefined} user the session user.
 * @param {"mine"|"all"|string} viewMode
 * @returns {"viewer"|"owner"|"none"}
 */
export function labelSourceFor(user, viewMode) {
  if (!user) return "none";
  if (user.is_admin && viewMode === "all") return "owner";
  if (isAccountLapsed(user)) return "none";
  return !user.is_admin || viewMode === "mine" ? "viewer" : "none";
}

// ---------------------------------------------------------------------------
// Admin - how a customer's label relates to the seeded defaults
// ---------------------------------------------------------------------------

const STARTER_BY_DEFAULT_KEY = new Map(STARTER_LABELS.map((s) => [s.defaultKey, s]));

/**
 * @param {{name: string, color: string, default_key?: string|null}} label
 * @returns {"default"|"edited"|"custom"} "edited" = a seeded default whose
 *   name or colour no longer matches what was seeded (the Hebrew name - the
 *   backend always seeds Hebrew).
 */
export function defaultLabelKind(label) {
  const starter = label?.default_key ? STARTER_BY_DEFAULT_KEY.get(label.default_key) : null;
  if (!starter) return "custom";
  const seededName = labelsText(true)[starter.copyKey];
  return label.name === seededName && label.color === starter.color ? "default" : "edited";
}

/**
 * The seeded defaults the customer has since deleted, in starter order.
 * Nothing before the account was seeded (`seededAt` null): nothing was
 * deleted, the defaults simply do not exist yet.
 *
 * @param {{default_key?: string|null}[]} labels
 * @param {string|null|undefined} seededAt
 * @returns {typeof STARTER_LABELS[number][]}
 */
export function deletedDefaults(labels, seededAt) {
  if (!seededAt) return [];
  const present = new Set((labels || []).map((label) => label?.default_key).filter(Boolean));
  return STARTER_LABELS.filter((s) => !present.has(s.defaultKey));
}
